/***
 *
 *   CLIENT ONBOARDING V2 — PUBLIC FORM
 *   The person-first onboarding form at /client-onboarding-v2/:id (KTD1). The
 *   client enters each person once, then each entity, then the role rows that
 *   link them (R5).
 *
 *   GET  /api/public/client-onboarding-v2/:id   { data: { schema, theme } },
 *        or { data: { closed: true, message } } when ops have not turned v2 on
 *   POST the same URL with { answers, submission_key }
 *
 *   The pickers, the draft and the completion gate live in
 *   client-onboarding-v2-pickers.js, where they are tested. From the v1 view
 *   (client-onboarding.js) this keeps the IndexedDB draft, uploads keyed by the
 *   persistent response id, theming, and the ABN/ACN/TFN validators. What is
 *   new: the draft never holds a TFN, an upload is stored under its party's id
 *   too, the POST happens in onCompleting so a refused submission keeps the
 *   form open, and the response id doubles as the submission key, so a
 *   resubmit from the same browser makes no second client group (R14, KTD7).
 *
 **********/

import { Loader, Grid, Card } from "components/lib";
import { applyFormTheme, useFormBrandColor } from "components/survey/formTheme";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { StylesManager, Model } from "survey-core";
import { Survey } from "survey-react-ui";
import Axios from "axios";
import { get, set, del } from "idb-keyval";
import { v4 as uuidv4 } from "uuid";
import { validatePoaUpload } from "./poa-upload-check";
import {
  watchPartyChoices,
  clearedSelectionMessages,
  restoreDraft,
  tfnQuestionNames,
  draftWithoutTfn,
  collectAnswers,
  createUploadHandler,
  createSubmitGate,
  groupErrorsByParty,
  submitOutcome,
} from "./client-onboarding-v2-pickers";
import "survey-core/defaultV2.min.css";

const Settings = require("settings.json");
const server_url = Settings[process.env.NODE_ENV].server_url;
const {
  validateABN,
  validateACN,
  validateTfn,
} = require("./australian-identifiers");

StylesManager.applyTheme("defaultV2");

// Separate from the v1 keys (clientOnboarding-<id>, formDraft-client-<id>), so
// neither view reads or clears the other's draft.
const responseIdKey = (id) => `clientOnboardingV2-${id}`;
const draftKey = (id) => `formDraftV2-client-${id}`;

// Every IndexedDB call goes through this. idb-keyval opens the database
// synchronously, so when indexedDB.open itself throws (Firefox with site data
// blocked, a sandboxed frame, no indexedDB at all), get/set/del throw before
// any promise exists. Here that throw becomes a rejection, which each caller
// already handles, instead of an error that unmounts the view or fails a
// submission the server has accepted.
const idb = (fn, ...args) => Promise.resolve().then(() => fn(...args));

// The response id, kept in IndexedDB so a reload resubmits under the same key.
// When IndexedDB refuses (a private window, blocked site data), the id lives
// in memory: the form still renders, and only a reload gets a new key.
const usePersistentResponseId = (id) => {
  const [responseId, setResponseId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let stored;
      try {
        stored = await idb(get, responseIdKey(id));
        if (!stored) {
          stored = uuidv4();
          await idb(set, responseIdKey(id), stored);
        }
      } catch {
        stored = stored || uuidv4();
      }
      if (!cancelled) setResponseId(stored);
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);
  return responseId;
};

// The ABN, ACN and TFN validators, as in the v1 view.
function validateQuestion(sender, options) {
  const question = options.question;
  const name = question.name || "";
  if (!options.value) return;

  if (name.toLowerCase().includes("abn")) {
    const error = validateABN(options.value);
    if (error) options.error = error;
  }
  if (!options.error && name.toLowerCase().includes("acn")) {
    const error = validateACN(options.value);
    if (error) options.error = error;
  }
  const isTfnQuestion = question.jsonObj?.tfn === true || /(^|_)tfn$/i.test(name);
  if (!options.error && isTfnQuestion) {
    const error = validateTfn(options.value);
    if (error) options.error = error;
  }
}

// One message for each load failure, as in the v1 view.
function loadError(err) {
  const status = err.response?.status;
  if (status === 404) return { title: "Onboarding form not found" };
  if (status === 403) return { title: "This onboarding form is not currently enabled" };
  if (status === 409) {
    return {
      title: "This onboarding form cannot be used at the moment",
      detail: err.response?.data?.message,
    };
  }
  return { title: "An error occurred loading the form" };
}

export function ClientOnboardingV2FormView() {
  const { id } = useParams();
  const [formData, setFormData] = useState(null);
  // `undefined` until the payload lands; see useFormBrandColor.
  const [formTheme, setFormTheme] = useState();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // frames the page in the form's own colour
  useFormBrandColor(formTheme, !loading);

  // the model whose draft is restored and whose pickers are filled (restoreDraft)
  const [readyModel, setReadyModel] = useState(null);
  // what blocked the last Complete: { message, errors }
  const [refusal, setRefusal] = useState(null);
  // cleared-selection messages, shown until the client dismisses them (R9)
  const [notices, setNotices] = useState([]);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const responseId = usePersistentResponseId(id);
  const refusalRef = useRef(null);

  // Fetch the form schema, or the closed message (R2).
  useEffect(() => {
    Axios.get(`/api/public/client-onboarding-v2/${id}`)
      .then((res) => {
        const data = res.data?.data || {};
        if (data.closed) {
          setError({ title: "This onboarding link is closed", detail: data.message });
          return;
        }
        // The theme first: a schema without its theme would build the model twice.
        setFormTheme(data.theme || null);
        setFormData(data.schema || null);
      })
      .catch((err) => setError(loadError(err)))
      .finally(() => setLoading(false));
  }, [id]);

  const tfnNames = useMemo(() => tfnQuestionNames(formData), [formData]);

  const model = useMemo(() => {
    if (!formData) return null;
    return applyFormTheme(new Model(JSON.parse(JSON.stringify(formData))), formTheme);
  }, [formData, formTheme]);

  // File upload handler: each file is stored under the persistent response id
  // and its party's id, and keeps the storage key the staff screens open it by
  // (createUploadHandler). The proof-of-address check reads the bare name.
  const uploadFiles = useMemo(
    () =>
      createUploadHandler({
        responseId,
        check: (options) =>
          validatePoaUpload({
            files: options.files,
            fieldName: options.name,
            formSchema: formData,
            onboardingId: id,
            serverUrl: server_url,
          }),
        post: (fd) => Axios.post(server_url + "/api/utility/gcp/upload", fd).then((res) => res.data),
      }),
    [formData, id, responseId]
  );

  // The POST. Resolves true only when the server accepted the submission; the
  // gate then completes the survey. Anything else keeps the form open.
  const submit = useCallback(
    async (answers) => {
      setRefusal(null);
      setBusy(true);
      let outcome;
      try {
        const res = await Axios.post(`/api/public/client-onboarding-v2/${id}`, {
          answers,
          submission_key: responseId,
        });
        outcome = submitOutcome(res.status, res.data);
      } catch (err) {
        outcome = submitOutcome(err.response?.status, err.response?.data);
      } finally {
        setBusy(false);
      }

      if (outcome.kind !== "accepted") {
        // 400: each error by party name. 403: the link closed while the client
        // was filling it in; the draft stays in this browser.
        setRefusal({ message: outcome.message, errors: outcome.errors || [] });
        return false;
      }

      // A new submission from this browser gets a new key.
      await Promise.all([idb(del, draftKey(id)), idb(del, responseIdKey(id))]).catch(() => {});
      setSubmitted({ message: outcome.message });
      // Only an absolute http(s) URL: submitOutcome drops any other redirect, a
      // javascript: or data: URL included, which would run on this origin.
      if (outcome.redirect) window.location.assign(outcome.redirect);
      return true;
    },
    [id, responseId]
  );

  // One gate for each model: it holds the in-flight flag.
  const onCompleting = useMemo(
    () =>
      createSubmitGate({
        collect: (sender) => collectAnswers(sender, responseId),
        submit,
        onRefused: setRefusal,
      }),
    [responseId, submit]
  );

  // Restore the draft and fill the pickers before the form renders, then keep
  // the pickers in sync and the draft saved while the client types. Once per
  // model: a second restore would put the saved draft over the live answers.
  useEffect(() => {
    if (!model) return undefined;
    let cancelled = false;
    let unwatch = () => {};

    const showCleared = (cleared) => {
      const messages = clearedSelectionMessages(cleared);
      setNotices((current) => [...current, ...messages.filter((m) => !current.includes(m))]);
    };
    // The draft never holds a TFN (R16).
    const saveDraft = () => {
      idb(set, draftKey(id), draftWithoutTfn(model.data, tfnNames)).catch(() => {});
    };

    idb(get, draftKey(id))
      .catch(() => null)
      .then((draft) => {
        if (cancelled) return;
        const { cleared } = restoreDraft(model, draft);
        // Saved again at once, so a reload does not restore the stale pick and
        // repeat the notice before the client changes anything.
        if (cleared.length) {
          showCleared(cleared);
          saveDraft();
        }
        unwatch = watchPartyChoices(model, { onCleared: showCleared, onSynced: saveDraft });
        setReadyModel(model);
      });

    return () => {
      cancelled = true;
      unwatch();
    };
  }, [model, id, tfnNames]);

  useEffect(() => {
    if (!model || !responseId) return undefined;
    model.onUploadFiles.add(uploadFiles);
    model.onValidateQuestion.add(validateQuestion);
    model.onCompleting.add(onCompleting);

    return () => {
      model.onUploadFiles.remove(uploadFiles);
      model.onValidateQuestion.remove(validateQuestion);
      model.onCompleting.remove(onCompleting);
    };
  }, [model, responseId, uploadFiles, onCompleting]);

  // A refusal shows above the form; bring it into view.
  useEffect(() => {
    if (refusal && refusalRef.current?.scrollIntoView) {
      refusalRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [refusal]);

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return (
      <div className="min-w-min w-3/4 mx-auto mt-12">
        <Grid>
          <Card>
            <div className="text-center py-12">
              <h2 className="text-xl font-semibold text-gray-700 mb-2">
                {error.title}
              </h2>
              <p className="text-gray-500">
                {error.detail ||
                  "Please check the URL and try again, or contact the firm that sent you this link."}
              </p>
            </div>
          </Card>
        </Grid>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-w-min w-3/4 mx-auto mt-12">
        <Grid>
          <Card>
            <div className="text-center py-12">
              <div className="text-5xl mb-4">&#10003;</div>
              <h2 className="text-xl font-semibold text-gray-700 mb-2">
                Onboarding Form Submitted
              </h2>
              <p className="text-gray-500">
                Thank you for completing the onboarding form. Your information
                has been received and is being processed.
              </p>
              {submitted.message && (
                <p className="text-gray-400 mt-4 text-sm">{submitted.message}</p>
              )}
            </div>
          </Card>
        </Grid>
      </div>
    );
  }

  if (!model || !responseId || readyModel !== model) {
    return <Loader />;
  }

  const errorGroups = groupErrorsByParty(refusal?.errors);

  return (
    <>
      <div className="min-w-min w-3/4">
        <Grid>
          <Card>
            {refusal && (
              <div
                ref={refusalRef}
                role="alert"
                className="mb-4 p-4 rounded-lg border border-red-200 bg-red-50 text-red-700 text-sm"
              >
                <p className="font-semibold">{refusal.message}</p>
                {errorGroups.length > 0 && (
                  <div className="mt-2 space-y-2">
                    {errorGroups.map((group) => (
                      <div key={group.partyName || "form"}>
                        <p className="font-medium">{group.partyName || "Other answers"}</p>
                        <ul className="pl-5 mt-1 space-y-1" style={{ listStyleType: "disc" }}>
                          {group.messages.map((message) => (
                            <li key={message}>{message}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            <Survey model={model} />
          </Card>
        </Grid>
      </div>
      {notices.length > 0 && (
        <div
          role="status"
          className="fixed bottom-2 left-4 right-4 z-50 max-w-2xl mx-auto p-4 rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-sm shadow-lg flex items-start gap-3"
        >
          <div className="flex-1 space-y-1">
            {notices.map((notice) => (
              <p key={notice}>{notice}</p>
            ))}
          </div>
          <button type="button" className="ml-auto underline" onClick={() => setNotices([])}>
            Dismiss
          </button>
        </div>
      )}
      {busy && (
        <div className="fixed inset-0 grid place-items-center bg-black/50 backdrop-blur-sm z-50">
          <div className="w-80 rounded-2xl bg-white shadow-2xl p-6 flex flex-col items-center gap-4">
            <h2 className="text-lg font-semibold text-gray-800">
              Submitting your form...
            </h2>
          </div>
        </div>
      )}
    </>
  );
}

export default ClientOnboardingV2FormView;
